import React, { Component } from "react";
import http from "../../modules/api";
import SaveDialogV2 from "../../modules/dialogs/SaveDialogV2";
import InstagramAutoSetupMarketingAnalysisModal from "./InstagramAutoSetupMarketingAnalysisModal";

const TAB_TYPE = Object.freeze({
  LINK: "link",
  COMPANY_URL: "company_url",
  FILE: "file",
  CONCEPT: "concept",
});

const ACCEPTED_FILE_EXTENSIONS = [".pdf", ".txt", ".docx"];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY = "instagram_auto_setup_analysis_result";
const AUTO_SETUP_PENDING_RESULT_ENTRY_KEY = "instagram_auto_setup_pending_result_entry";
const AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY =
  "instagram_auto_setup_analysis_company_profile_seq";
const AUTO_SETUP_RESULT_ROUTE =
  "/shop/instagram/InstagramAutoSetupResult";

const AUTO_SETUP_GPT_LIMIT_CODES = [
  "GPT_CALL_LIMIT", "GPT_WEB_SEARCH_CALL_LIMIT", "GPT_CALL_LIMIT_CHECK_ERROR",
  "GPT_WEB_SEARCH_LIMIT_CHECK_ERROR", "GPT_WEB_SEARCH_LIMIT_PERIOD_NOT_FOUND",
];

const getAutoSetupApiErrorMessage = (value, fallback) => {
  const data = value?.response?.data || value || {};
  const code = String(data.error_code || data.reason || "");
  const detail = data.message || data.error_msg;
  if (AUTO_SETUP_GPT_LIMIT_CODES.includes(code)) {
    return detail || "GPT 사용량을 확인할 수 없거나 사용 가능한 사용량이 부족합니다.";
  }
  return detail || fallback;
};

const SAVE_DIALOG_BUTTON_LIST = [
  {
    title: "확인",
    type: "1",
    display_yn: "N",
  },
];

class InstagramAutoSetupStarter extends Component {
  constructor(props) {
    super(props);

    this.fileInputRef = React.createRef();
    this.dragDepth = 0;

    this.state = {
      // 첫 번째 탭(컨셉/아이디어)을 기본 선택
      activeTab: TAB_TYPE.CONCEPT,
      productLink: "",
      companyUrl: "",
      businessPlanFile: null,
      conceptInput: "",
      isDragging: false,
      isSubmitting: false,
      errorMessage: "",
      marketingResult: null,
      marketingPayload: null,
      marketingChanged: false,
      historyList: [],
      isHistoryLoading: false,
      historyError: "",
      openingHistorySeq: null,
      activeHistoryMenuSeq: null,
      historyActionSeq: null,
      visibleHistoryCount: 4,
    };
  }

  componentDidMount() {
    this.loadAnalysisHistory();
    document.addEventListener("pointerdown", this.closeHistoryMenuOnOutsideClick);
  }

  componentWillUnmount() {
    document.removeEventListener("pointerdown", this.closeHistoryMenuOnOutsideClick);
  }

  closeHistoryMenuOnOutsideClick = (event) => {
    if (!event.target.closest || !event.target.closest(".auto-setup-history-menu-wrap")) {
      if (this.state.activeHistoryMenuSeq !== null) this.setState({ activeHistoryMenuSeq: null });
    }
  };

  createAnalysisHistoryRequestId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
    return `analysis-copy-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  };

  getAnalysisHistoryDetail = async (seq) => {
    const response = await http.get("/rest/shop/instagram/auto-setup/analysis/detail", {
      params: { auto_setup_analysis_seq: seq },
    });
    const data = response?.data || {};
    if (data.status !== "success" || !data.analysis_result) {
      throw new Error(data.message || "분석 보고서를 불러올 수 없습니다.");
    }
    return data;
  };

  navigateToAnalysisResult = (result) => {
    if (typeof this.props.onAnalyzeComplete === "function") {
      this.props.onAnalyzeComplete(result, null);
      return;
    }
    const resultUrl = new URL(AUTO_SETUP_RESULT_ROUTE, window.location.origin);
    resultUrl.searchParams.set("step", "2");
    const profileSeq = this.props.companyProfileSeq || this.props.company_profile_seq;
    if (profileSeq) resultUrl.searchParams.set("company_profile_seq", String(profileSeq));
    window.location.href = `${resultUrl.pathname}${resultUrl.search}`;
  };

  loadAnalysisHistory = async () => {
    this.setState({ isHistoryLoading: true, historyError: "" });
    try {
      const response = await http.get("/rest/shop/instagram/auto-setup/analysis/list", {
        params: { limit: 30 },
      });
      const data = response?.data || {};
      if (data.status !== "success") throw new Error(data.message || "분석 이력 조회 실패");
      this.setState({ historyList: Array.isArray(data.list) ? data.list : [], isHistoryLoading: false });
    } catch (error) {
      this.setState({ isHistoryLoading: false, historyError: error?.message || "분석 이력을 불러오지 못했습니다." });
    }
  };

  openAnalysisHistory = async (seq, openEditor = false) => {
    if (this.state.isSubmitting || this.state.openingHistorySeq || this.state.historyActionSeq) return;
    this.setState({ openingHistorySeq: seq, activeHistoryMenuSeq: null, historyError: "" });
    try {
      const data = await this.getAnalysisHistoryDetail(seq);
      const result = {
        ...data.analysis_result,
        auto_setup_analysis_seq: data.auto_setup_analysis_seq,
        opened_from_history: true,
      };
      // 등록 작업 ID가 남아 있으면 과거 콘텐츠/자동화 작업을 재사용하게 되므로 제거한다.
      ["setup_request_id", "content_registration_request_id", "ui_copywriter_preset_seq",
       "ui_completed_actions", "company_repository_seq", "setting_seq", "preset_seq"].forEach(k => delete result[k]);
      this.persistMarketingResult(result);
      try {
        sessionStorage.setItem(AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY,
          String(this.props.companyProfileSeq || this.props.company_profile_seq || ""));
      } catch (error) { console.warn("[InstagramAutoSetupStarter] 프로필 컨텍스트 저장 실패", error); }
      if (openEditor) {
        // '수정'은 보고서 팝업이 아니라 최종 피드 편집 화면으로 바로 이동한다.
        this.openedHistoryOriginal = null;
        this.setState({ marketingResult: null, marketingPayload: null, marketingChanged: false,
          openingHistorySeq: null }, () => this.navigateToAnalysisResult(result));
      } else {
        // 카드 클릭은 보고서를 조회한다. 열기만으로 DB 저장/이미지 생성 없음.
        this.openedHistoryOriginal = { ...result };
        this.setState({ marketingResult: result, marketingPayload: null, marketingChanged: false,
          openingHistorySeq: null });
      }
    } catch (error) {
      this.setState({ openingHistorySeq: null, historyError: error?.message || "분석 이력을 열지 못했습니다." });
    }
  };

  toggleAnalysisHistoryMenu = (seq) => {
    // 유효한 분석 번호가 없는 카드에서는 메뉴가 초기 표시되지 않도록 한다.
    if (!Number.isSafeInteger(Number(seq)) || Number(seq) <= 0) return;
    if (this.state.historyActionSeq || this.state.openingHistorySeq) return;
    this.setState(prev => ({ activeHistoryMenuSeq: prev.activeHistoryMenuSeq === seq ? null : seq }));
  };

  setRepresentativeAnalysis = async (item) => {
    const seq = item.auto_setup_analysis_seq;
    if (this.state.historyActionSeq) return;
    this.setState({ historyActionSeq: seq, activeHistoryMenuSeq: null, historyError: "" });
    try {
      const response = await http.post("/rest/shop/instagram/auto-setup/analysis/representative", {
        auto_setup_analysis_seq: seq,
      });
      const data = response?.data || {};
      if (data.status !== "success") throw new Error(data.message || "대표 지정에 실패했습니다.");
      await this.loadAnalysisHistory();
    } catch (error) {
      this.setState({ historyError: error?.response?.data?.message || error?.message || "대표 지정에 실패했습니다." });
    } finally {
      this.setState({ historyActionSeq: null });
    }
  };

  duplicateAnalysisHistory = async (item) => {
    const seq = item.auto_setup_analysis_seq;
    if (this.state.historyActionSeq) return;
    this.setState({ historyActionSeq: seq, activeHistoryMenuSeq: null, historyError: "" });
    try {
      const original = await this.getAnalysisHistoryDetail(seq);
      const source = original.analysis_result;
      const clone = {
        ...source,
        analysis_title: `${(item.analysis_title || "기업 분석").slice(0, 190)} (복사본)`,
      };
      delete clone.company_profile_seq;
      const response = await http.post("/rest/shop/instagram/auto-setup/analysis/save", {
        fork_from_analysis_seq: seq,
        fork_request_id: this.createAnalysisHistoryRequestId(),
        analysis_result: clone,
      });
      const data = response?.data || {};
      if (data.status !== "success" || !data.auto_setup_analysis_seq) {
        throw new Error(data.message || "기업 분석 복제에 실패했습니다.");
      }
      await this.loadAnalysisHistory();
    } catch (error) {
      this.setState({ historyError: error?.response?.data?.message || error?.message || "기업 분석 복제에 실패했습니다." });
    } finally {
      this.setState({ historyActionSeq: null });
    }
  };

  deleteAnalysisHistory = async (item) => {
    const seq = item.auto_setup_analysis_seq;
    if (this.state.historyActionSeq) return;
    this.setState({ activeHistoryMenuSeq: null });
    if (!window.confirm(`'${item.analysis_title || "기업 분석"}' 보고서를 삭제하시겠습니까?\n등록된 콘텐츠와 자동화는 삭제되지 않습니다.`)) return;
    this.setState({ historyActionSeq: seq, historyError: "" });
    try {
      const response = await http.post("/rest/shop/instagram/auto-setup/analysis/delete", {
        auto_setup_analysis_seq: seq,
      });
      const data = response?.data || {};
      if (data.status !== "success") throw new Error(data.message || "분석 보고서 삭제에 실패했습니다.");
      // 삭제한 보고서가 임시 저장돼 있다면 다음 진입에서 다시 복원하지 않는다.
      try {
        const saved = JSON.parse(sessionStorage.getItem(AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY) || "null");
        if (Number(saved?.auto_setup_analysis_seq) === Number(seq)) {
          sessionStorage.removeItem(AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY);
        }
      } catch (error) { console.warn("[InstagramAutoSetupStarter] 삭제 보고서 캐시 정리 실패", error); }
      await this.loadAnalysisHistory();
    } catch (error) {
      this.setState({ historyError: error?.response?.data?.message || error?.message || "분석 보고서 삭제에 실패했습니다." });
    } finally {
      this.setState({ historyActionSeq: null });
    }
  };

  saveMarketingEdits = async (result) => {
    if (!result?.auto_setup_analysis_seq) return;
    const response = await http.post("/rest/shop/instagram/auto-setup/analysis/save", {
      auto_setup_analysis_seq: result.auto_setup_analysis_seq,
      analysis_result: result,
    });
    const data = response?.data || {};
    if (data.status !== "success") throw new Error(data.message || "기업 마케팅 보고서를 저장하지 못했습니다.");
  };

  renderAnalysisHistory = () => (
    <section className="auto-setup-history" aria-label="AI 기획 자동화 목록">
      <div className="auto-setup-history-heading">
        <div>
          <h2>AI 기획 자동화 목록 <span className="auto-setup-history-new">NEW</span></h2>
          <p>생성된 AI 기획 자동화가 여기 저장됩니다. 원하는 항목을 선택하여 확인하거나 수정할 수 있습니다.</p>
        </div>
        <button type="button" className="auto-setup-history-refresh" onClick={this.loadAnalysisHistory}
          disabled={this.state.isHistoryLoading || Boolean(this.state.openingHistorySeq) || Boolean(this.state.historyActionSeq)}>
          새로고침
        </button>
      </div>
      {this.state.historyError && <p role="alert" className="auto-setup-history-error">{this.state.historyError}</p>}
      {this.state.isHistoryLoading && !this.state.historyList.length ?
        <p className="auto-setup-history-empty">분석 보고서를 불러오는 중...</p> :
        !this.state.historyList.length ?
          <p className="auto-setup-history-empty">저장된 기업 분석 보고서가 없습니다.</p> :
          <div className="auto-setup-history-grid">
            {this.state.historyList.slice(0, this.state.visibleHistoryCount).map(item => {
              const seq = item.auto_setup_analysis_seq;
              // 최초 렌더 시에는 메뉴가 반드시 닫혀 있다. 더보기 클릭한 카드만 열린다.
              const isMenuOpen = Number.isSafeInteger(Number(seq)) && Number(seq) > 0
                && this.state.activeHistoryMenuSeq !== null
                && this.state.activeHistoryMenuSeq === seq;
              const representative = item.is_representative === "Y";
              const busy = this.state.openingHistorySeq === seq || this.state.historyActionSeq === seq;
              return (
                <div key={seq} className={`auto-setup-history-card${representative ? " is-representative" : ""}`}>
                  <div className="auto-setup-history-card-actions">
                    {representative ? <span className="auto-setup-history-featured" aria-label="대표 보고서">★ 대표</span> :
                      <button type="button" className="auto-setup-history-set-featured"
                        disabled={Boolean(this.state.historyActionSeq)}
                        onClick={() => this.setRepresentativeAnalysis(item)}>☆ 대표 지정</button>}
                    <div className="auto-setup-history-menu-wrap">
                      <button type="button" className="auto-setup-history-more"
                        aria-label={`${item.analysis_title || "기업 분석"} 더보기`}
                        aria-expanded={isMenuOpen}
                        disabled={Boolean(this.state.historyActionSeq) || Boolean(this.state.openingHistorySeq)}
                        onClick={() => this.toggleAnalysisHistoryMenu(seq)}>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                          </svg>
                        </button>
                      {isMenuOpen && (
                        <div className="auto-setup-history-popup is-open" role="menu">
                          <button type="button" role="menuitem" onClick={() => this.openAnalysisHistory(seq, true)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="m16 4 4 4L8 20H4v-4L16 4Z"/></svg><span>수정</span>
                          </button>
                          <button type="button" role="menuitem" onClick={() => this.duplicateAnalysisHistory(item)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3"/></svg><span>복제</span>
                          </button>
                          <button type="button" role="menuitem" className="danger" onClick={() => this.deleteAnalysisHistory(item)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M6 6l1 15h10l1-15M10 10v7M14 10v7"/></svg><span>삭제</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <button type="button" className="auto-setup-history-card-open"
                    disabled={Boolean(this.state.openingHistorySeq) || Boolean(this.state.historyActionSeq) || this.state.isSubmitting}
                    onClick={() => this.openAnalysisHistory(seq)}>
                    <span className="auto-setup-history-document" aria-hidden="true">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </span>
                    <span className="auto-setup-history-card-copy">
                      <strong title={item.analysis_title || "기업 분석"}>{item.analysis_title || "기업 분석"}</strong>
                      <span>{String(item.reg_date || "").replace(/-/g, ".")}</span>
                    </span>
                    <span className="auto-setup-history-card-arrow" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
                    </span>
                    {this.state.openingHistorySeq === seq && <span className="auto-setup-history-card-busy">여는 중...</span>}
                    {this.state.historyActionSeq === seq && <span className="auto-setup-history-card-busy">처리 중...</span>}
                  </button>
                </div>
              );
            })}
          </div>}
      {this.state.historyList.length > 4 && (
        <div className="auto-setup-history-footer">
          {this.state.visibleHistoryCount < this.state.historyList.length ?
            <button type="button" onClick={() => this.setState(prev => ({visibleHistoryCount: prev.visibleHistoryCount + 4}))}>
              더 보기 ({this.state.historyList.length - this.state.visibleHistoryCount}개 남음)
            </button> :
            <button type="button" onClick={() => this.setState({visibleHistoryCount: 4})}>접기</button>}
        </div>
      )}
    </section>
  );

  persistMarketingResult = (result) => {
    try {
      sessionStorage.setItem(AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY, JSON.stringify(result));
    } catch (error) {
      console.warn("[InstagramAutoSetupStarter] 마케팅 분석 편집본 보관 실패", error);
    }
  };

  handleMarketingChange = (result) => {
    this.setState({ marketingResult: result, marketingChanged: true });
    this.persistMarketingResult(result);
  };

  handleMarketingClose = () => {
    // 기존 분석을 열어보기만 하고 닫았다면 편집 중의 임시 변경도 폐기한다.
    if (this.state.marketingResult?.opened_from_history && this.openedHistoryOriginal) {
      this.persistMarketingResult(this.openedHistoryOriginal);
    }
    this.openedHistoryOriginal = null;
    this.setState({ marketingResult: null, marketingPayload: null, marketingChanged: false });
  };

  handleMarketingContinue = async (result) => {
    const payload = this.state.marketingPayload;
    try {
      // 이전 이력의 단순 조회는 UPDATE 하지 않는다. 실제 편집이 있는 경우만 저장한다.
      if (this.state.marketingChanged) await this.saveMarketingEdits(result);
      this.persistMarketingResult(result);
      this.openedHistoryOriginal = null;
      this.setState({ marketingResult: null, marketingPayload: null, marketingChanged: false }, () => {
        if (typeof this.props.onAnalyzeComplete === "function") {
          this.props.onAnalyzeComplete(result, payload);
        } else {
          this.navigateToAnalysisResult(result);
        }
      });
    } catch (error) {
      this.setState({ historyError: error?.message || "마케팅 보고서 저장에 실패했습니다." });
      window.alert(error?.message || "마케팅 보고서 저장에 실패했습니다.");
    }
  };

  switchTab = (activeTab) => {
    if (!Object.values(TAB_TYPE).includes(activeTab)) return;

    this.setState({
      activeTab,
      errorMessage: "",
    });
  };

  handleProductLinkChange = (event) => {
    this.setState({
      productLink: event.target.value,
      errorMessage: "",
    });
  };

  handleCompanyUrlChange = (event) => {
    this.setState({
      companyUrl: event.target.value,
      errorMessage: "",
    });
  };

  handleConceptChange = (event) => {
    this.setState({
      conceptInput: event.target.value,
      errorMessage: "",
    });
  };

  getFileExtension = (fileName = "") => {
    const normalizedName = String(fileName || "").toLowerCase();
    const dotIndex = normalizedName.lastIndexOf(".");
    return dotIndex >= 0 ? normalizedName.slice(dotIndex) : "";
  };

  validateFile = (file) => {
    if (!file) {
      return "사업계획서 파일을 선택해주세요.";
    }

    const extension = this.getFileExtension(file.name);
    if (!ACCEPTED_FILE_EXTENSIONS.includes(extension)) {
      return "PDF, TXT, DOCX 파일만 업로드할 수 있습니다.";
    }

    if (file.size > MAX_FILE_SIZE) {
      return "사업계획서 파일은 20MB 이하만 업로드할 수 있습니다.";
    }

    return "";
  };

  setBusinessPlanFile = (file) => {
    const errorMessage = this.validateFile(file);

    if (errorMessage) {
      this.setState({
        businessPlanFile: null,
        errorMessage,
        isDragging: false,
      });

      if (this.fileInputRef.current) {
        this.fileInputRef.current.value = "";
      }
      return;
    }

    this.setState({
      businessPlanFile: file,
      errorMessage: "",
      isDragging: false,
    });
  };

  handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    this.setBusinessPlanFile(file);
  };

  handleFileAreaClick = () => {
    if (this.state.isSubmitting) return;
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  handleFileKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.handleFileAreaClick();
    }
  };

  handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.dragDepth += 1;
    if (!this.state.isDragging) {
      this.setState({ isDragging: true });
    }
  };

  handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  };

  handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.setState({ isDragging: false });
    }
  };

  handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.dragDepth = 0;

    const file =
      event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files[0];

    if (!file) {
      this.setState({ isDragging: false });
      return;
    }

    this.setBusinessPlanFile(file);
  };

  // 프로토콜 생략 시 HTTPS를 자동으로 보완한다.
  normalizeWebsiteUrl = (value) => {
    const trimmedUrl = String(value || "").trim();
    if (!trimmedUrl) return "";

    return /^https?:\/\//i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;
  };

  validateWebsiteUrl = (value, emptyMessage, invalidMessage) => {
    const trimmedUrl = String(value || "").trim();
    if (!trimmedUrl) return emptyMessage;

    // 공백, 역슬래시, 미지원 프로토콜 및 불완전한 http(s) 구문은 허용하지 않는다.
    if (
      /\s|\\/.test(trimmedUrl) ||
      (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmedUrl) && !/^https?:\/\//i.test(trimmedUrl)) ||
      (/^https?:/i.test(trimmedUrl) && !/^https?:\/\//i.test(trimmedUrl)) ||
      trimmedUrl.startsWith("//")
    ) {
      return invalidMessage;
    }

    try {
      const parsedUrl = new URL(this.normalizeWebsiteUrl(trimmedUrl));
      const hostname = parsedUrl.hostname;

      // URL 객체는 example, a..com, 아이피 등도 파싱할 수 있으므로 도메인 형식을 따로 확인한다.
      if (
        !["http:", "https:"].includes(parsedUrl.protocol) ||
        !hostname ||
        hostname.length > 253 ||
        parsedUrl.username ||
        parsedUrl.password
      ) {
        return invalidMessage;
      }

      const labels = hostname.split(".");
      if (
        labels.length < 2 ||
        labels.some((label) =>
          label.length > 63 ||
          !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label)
        ) ||
        !/^(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/i.test(labels[labels.length - 1])
      ) {
        return invalidMessage;
      }
    } catch (error) {
      return invalidMessage;
    }

    return "";
  };

  validateBeforeSubmit = () => {
    const {
      activeTab,
      productLink,
      companyUrl,
      businessPlanFile,
      conceptInput,
    } = this.state;

    if (activeTab === TAB_TYPE.LINK) {
      return this.validateWebsiteUrl(
        productLink,
        "상품 링크를 입력해주세요.",
        "올바른 상품 링크를 입력해주세요."
      );
    }

    if (activeTab === TAB_TYPE.COMPANY_URL) {
      return this.validateWebsiteUrl(
        companyUrl,
        "회사 홈페이지 URL을 입력해주세요.",
        "올바른 회사 홈페이지 URL을 입력해주세요."
      );
    }

    if (activeTab === TAB_TYPE.FILE) {
      return this.validateFile(businessPlanFile);
    }

    if (activeTab === TAB_TYPE.CONCEPT && !conceptInput.trim()) {
      return "인스타그램 컨셉이나 아이디어를 입력해주세요.";
    }

    return "";
  };

  buildAnalyzePayload = () => {
    const {
      activeTab,
      productLink,
      companyUrl,
      businessPlanFile,
      conceptInput,
    } = this.state;

    return {
      inputType: activeTab.toUpperCase(),
      productLink:
        activeTab === TAB_TYPE.LINK ? this.normalizeWebsiteUrl(productLink) : "",
      companyUrl:
        activeTab === TAB_TYPE.COMPANY_URL ? this.normalizeWebsiteUrl(companyUrl) : "",
      businessPlanFile:
        activeTab === TAB_TYPE.FILE ? businessPlanFile : null,
      concept:
        activeTab === TAB_TYPE.CONCEPT ? conceptInput.trim() : "",
    };
  };

  requestAutoSetupAnalyze = async (payload) => {
    const formData = new FormData();

    formData.append("input_type", payload.inputType);

    if (payload.inputType === "LINK") {
      formData.append("product_link", payload.productLink || "");
    }
    if (payload.inputType === "COMPANY_URL") {
      formData.append("company_url", payload.companyUrl || "");
    }

    if (
      payload.inputType === "FILE" &&
      payload.businessPlanFile
    ) {
      formData.append(
        "business_plan_file",
        payload.businessPlanFile
      );
    }

    if (payload.inputType === "CONCEPT") {
      formData.append("concept", payload.concept || "");
    }

    const response = await http.post(
      "/rest/shop/instagram/auto-setup/analyze",
      formData
    );

    const data = response?.data || {};

    this.validateAnalyzeResult(data, payload);
    return data;
  };

  validateAnalyzeResult = (result, payload) => {
    if (!result || result.status !== "success" || !result.analysis?.source_analysis) {
      throw new Error(getAutoSetupApiErrorMessage(result, "AI 분석에 실패했습니다."));
    }
    if (!result.analysis?.marketing_report) {
      throw new Error("마케팅 전략 보고서 결과가 없습니다. 분석을 다시 실행해주세요.");
    }
    if (payload?.inputType === "COMPANY_URL") {
      if (result.source?.url_analysis_status !== "SUCCESS") {
        throw new Error("회사 URL 분석에 실패했습니다. 회사 정보를 확인할 수 없습니다.");
      }
    }
    if (payload?.inputType === "LINK") {
      if (result.source?.url_analysis_status !== "SUCCESS") {
        throw new Error("상품 URL 분석에 실패했습니다. 상품 정보를 확인할 수 없어 결과 화면으로 이동하지 않습니다.");
      }
      const sourceAnalysis = result.analysis.source_analysis;
      const hasSubject = ["products", "services"].some((key) =>
        Array.isArray(sourceAnalysis[key]) &&
        sourceAnalysis[key].some((value) => String(value || "").trim())
      );
      if (!hasSubject) {
        throw new Error("상품 URL에서 상품 또는 서비스 정보를 확인하지 못했습니다.");
      }
    }
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    if (this.state.isSubmitting) return;

    const errorMessage = this.validateBeforeSubmit();
    if (errorMessage) {
      this.setState({ errorMessage });
      return;
    }

    this.setState({
      isSubmitting: true,
      errorMessage: "",
    });

    // 이전 분석 결과가 남아 실패한 요청에서 재사용되지 않도록 제거한다.
    try {
      sessionStorage.removeItem(AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY);
      sessionStorage.removeItem(AUTO_SETUP_PENDING_RESULT_ENTRY_KEY);
      sessionStorage.removeItem(AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY);
    } catch (storageError) {
      console.warn("[InstagramAutoSetupStarter] 이전 결과 초기화 실패", storageError);
    }

    try {
      const payload = this.buildAnalyzePayload();

      const result =
        typeof this.props.onAnalyze === "function"
          ? await this.props.onAnalyze(payload)
          : await this.requestAutoSetupAnalyze(payload);

      // 부모 onAnalyze 경로로 받은 응답도 동일하게 검사한다.
      this.validateAnalyzeResult(result, payload);

      try {
        sessionStorage.setItem(
          AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY,
          JSON.stringify(result)
        );
        sessionStorage.setItem(
          AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY,
          String(this.props.companyProfileSeq || this.props.company_profile_seq || "")
        );
      } catch (storageError) {
        console.warn(
          "[InstagramAutoSetupStarter] 분석 결과 임시 저장 실패",
          storageError
        );
      }

      // GPT 분석이 완료되면 Result보다 먼저 마케팅 보고서 팝업을 표시한다.
      this.setState({ marketingResult: result, marketingPayload: payload, marketingChanged: false });
    } catch (error) {
      const fallback = this.state.activeTab === TAB_TYPE.LINK && error?.response
        ? "상품 URL 분석에 실패했습니다. 상품 정보를 확인할 수 없습니다."
        : this.state.activeTab === TAB_TYPE.COMPANY_URL && error?.response
          ? "회사 URL 분석에 실패했습니다. 홈페이지 정보를 확인할 수 없습니다."
          : error?.message || "AI 분석 요청 중 오류가 발생했습니다.";
      const message = getAutoSetupApiErrorMessage(error, fallback);

      this.setState({
        errorMessage: message,
      });
    } finally {
      this.setState({
        isSubmitting: false,
      });
    }
  };

  handleLoginClick = () => {
    if (typeof this.props.onLogin === "function") {
      this.props.onLogin();
    }
  };

  handleFreeStartClick = () => {
    if (typeof this.props.onFreeStart === "function") {
      this.props.onFreeStart();
    }
  };

  renderArrowIcon() {
    return (
      <svg
        className="starter-arrow-icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M14 5l7 7m0 0l-7 7m7-7H3"
        />
      </svg>
    );
  }

  renderLinkIcon() {
    return (
      <svg
        className="starter-input-icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
        />
      </svg>
    );
  }

  renderUploadIcon() {
    return (
      <svg
        className="starter-upload-icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>
    );
  }

  renderTabButton(type, label) {
    const isActive = this.state.activeTab === type;

    return (
      <button
        type="button"
        className={`starter-tab-button ${isActive ? "is-active" : ""}`}
        onClick={() => this.switchTab(type)}
        aria-pressed={isActive}
        disabled={this.state.isSubmitting}
      >
        {label}
      </button>
    );
  }

  renderLinkPanel() {
    const { productLink, isSubmitting } = this.state;

    return (
      <div className="starter-panel">
        <div className="starter-action-row">
          <label className="starter-link-input-wrap">
            <span className="starter-visually-hidden">상품 링크</span>
            <span className="starter-input-icon-wrap">
              {this.renderLinkIcon()}
            </span>
            <input
              type="text"
              inputMode="url"
              className="starter-link-input"
              value={productLink}
              onChange={this.handleProductLinkChange}
              placeholder="example.com/product/123"
              disabled={isSubmitting}
              autoComplete="off"
            />
          </label>

          <button
            type="submit"
            className="starter-submit-button"
            disabled={isSubmitting}
          >
            <span>
              {isSubmitting ? "분석 중..." : "AI 생성하기"}
            </span>
            {!isSubmitting && this.renderArrowIcon()}
          </button>
        </div>
      </div>
    );
  }

  renderCompanyUrlPanel() {
    const { companyUrl, isSubmitting } = this.state;

    return (
      <div className="starter-panel">
        <div className="starter-action-row">
          <label className="starter-link-input-wrap">
            <span className="starter-visually-hidden">회사 홈페이지 URL</span>
            <span className="starter-input-icon-wrap">
              {this.renderLinkIcon()}
            </span>
            <input
              type="text"
              inputMode="url"
              className="starter-link-input"
              value={companyUrl}
              onChange={this.handleCompanyUrlChange}
              placeholder="www.company.com 또는 https://www.company.com"
              disabled={isSubmitting}
              autoComplete="off"
            />
          </label>

          <button
            type="submit"
            className="starter-submit-button"
            disabled={isSubmitting}
          >
            <span>{isSubmitting ? "분석 중..." : "AI 생성하기"}</span>
            {!isSubmitting && this.renderArrowIcon()}
          </button>
        </div>
      </div>
    );
  }

  renderFilePanel() {
    const {
      businessPlanFile,
      isDragging,
      isSubmitting,
    } = this.state;

    const fileName = businessPlanFile
      ? businessPlanFile.name
      : "사업계획서 파일(PDF, DOCX)을 드래그하거나 선택하세요";

    return (
      <div className="starter-panel">
        <div className="starter-action-row">
          <div
            className={[
              "starter-file-dropzone",
              isDragging ? "is-dragging" : "",
              businessPlanFile ? "has-file" : "",
            ].join(" ")}
            onClick={this.handleFileAreaClick}
            onKeyDown={this.handleFileKeyDown}
            onDragEnter={this.handleDragEnter}
            onDragOver={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
            role="button"
            tabIndex={isSubmitting ? -1 : 0}
            aria-disabled={isSubmitting}
          >
            <input
              ref={this.fileInputRef}
              type="file"
              accept=".pdf,.txt,.docx"
              className="starter-file-input"
              onChange={this.handleFileChange}
              disabled={isSubmitting}
            />

            <span className="starter-upload-icon-wrap">
              {this.renderUploadIcon()}
            </span>

            <span className="starter-file-name">
              {fileName}
            </span>
          </div>

          <button
            type="submit"
            className="starter-submit-button"
            disabled={isSubmitting}
          >
            <span>
              {isSubmitting ? "분석 중..." : "AI 생성하기"}
            </span>
            {!isSubmitting && this.renderArrowIcon()}
          </button>
        </div>
      </div>
    );
  }

  renderConceptPanel() {
    const { conceptInput, isSubmitting } = this.state;

    return (
      <div className="starter-panel">
        <textarea
          className="starter-concept-input"
          rows={4}
          value={conceptInput}
          onChange={this.handleConceptChange}
          placeholder="원하시는 인스타그램 컨셉이나 아이디어를 자유롭게 적어주세요. (예: 20대 여성을 위한 미니멀 패션 브랜드, 감성적인 무드의 홈카페 인테리어 팁 등)"
          disabled={isSubmitting}
        />

        <div className="starter-concept-action">
          <button
            type="submit"
            className="starter-submit-button"
            disabled={isSubmitting}
          >
            <span>
              {isSubmitting
                ? "분석 중..."
                : "컨셉으로 피드 만들기"}
            </span>
            {!isSubmitting && this.renderArrowIcon()}
          </button>
        </div>
      </div>
    );
  }

  renderActivePanel() {
    switch (this.state.activeTab) {
      case TAB_TYPE.COMPANY_URL:
        return this.renderCompanyUrlPanel();
      case TAB_TYPE.FILE:
        return this.renderFilePanel();
      case TAB_TYPE.CONCEPT:
        return this.renderConceptPanel();
      case TAB_TYPE.LINK:
      default:
        return this.renderLinkPanel();
    }
  }

  render() {
    const { errorMessage, isSubmitting } = this.state;

    return (
      <div className="InstagramAutoSetupStarter">
        <style>{`
          .InstagramAutoSetupStarter label {
            margin: 0 !important;
          }
          .InstagramAutoSetupStarter {
            --starter-bg: #f6f8fc;
            --starter-card: #ffffff;
            --starter-text: #172033;
            --starter-muted: #667085;
            --starter-border: #dfe5ee;
            --starter-border-strong: #cfd7e4;
            --starter-primary: #4f46e5;
            --starter-primary-hover: #4338ca;
            --starter-primary-soft: #eef2ff;
            --starter-cyan-soft: #ecfeff;
            --starter-success: #18794e;
            --starter-danger: #b42318;
            --starter-danger-bg: #fff4f2;

            width: 100%;
            height: 100%;
            min-height: 0;

            background: var(--starter-bg);
            color: var(--starter-text);
            font-family:
              Pretendard,
              -apple-system,
              BlinkMacSystemFont,
              "Segoe UI",
              sans-serif;
          }

          .InstagramAutoSetupStarter * {
            box-sizing: border-box;
          }

          .InstagramAutoSetupStarter .starter-shell {
            display: flex;
            flex-direction: column;
            
            width: 100%;
            height: 100%;
            min-height: 0;
          }

          .InstagramAutoSetupStarter .starter-header {
            width: 100%;
            background: rgba(255, 255, 255, 0.92);
            border-bottom: 1px solid #edf0f5;
            backdrop-filter: blur(12px);
          }

          .InstagramAutoSetupStarter .starter-header-inner {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            max-width: 1240px;
            margin: 0 auto;
            padding: 18px 28px;
          }

          .InstagramAutoSetupStarter .starter-brand {
            display: flex;
            align-items: center;
            gap: 9px;
          }

          .InstagramAutoSetupStarter .starter-brand-name {
            font-size: 24px;
            font-weight: 900;
            letter-spacing: -0.04em;
            background: linear-gradient(
              90deg,
              #4f46e5 0%,
              #7c3aed 48%,
              #0891b2 100%
            );
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
          }

          .InstagramAutoSetupStarter .starter-brand-badge {
            padding: 4px 8px;
            border: 1px solid #d9ddff;
            border-radius: 999px;
            background: #f3f4ff;
            color: #4f46e5;
            font-size: 11px;
            font-weight: 800;
          }

          .InstagramAutoSetupStarter .starter-header-actions {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .InstagramAutoSetupStarter .starter-header-button {
            min-height: 38px;
            padding: 0 13px;
            border-radius: 9px;
            border: 1px solid transparent;
            background: transparent;
            color: #475467;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
          }

          .InstagramAutoSetupStarter .starter-header-button:hover {
            background: #f7f8fb;
            color: #1d2939;
          }

          .InstagramAutoSetupStarter .starter-header-button.primary {
            padding: 0 16px;
            border-color: #4f46e5;
            background: #4f46e5;
            color: #ffffff;
            box-shadow: 0 6px 16px rgba(79, 70, 229, 0.16);
          }

          .InstagramAutoSetupStarter
            .starter-header-button.primary:hover {
            background: #4338ca;
            color: #ffffff;
          }

          .InstagramAutoSetupStarter .starter-main {
            flex: 1;
            display: flex;

            width: 100%;
            min-height: 0;
          }

          .InstagramAutoSetupStarter .starter-footer {
            width: 100%;
            padding: 22px 18px;
            border-top: 1px solid #edf0f5;
            background: #ffffff;
            color: #98a2b3;
            text-align: center;
            font-size: 12px;
          }

          .InstagramAutoSetupStarter button,
          .InstagramAutoSetupStarter input,
          .InstagramAutoSetupStarter textarea {
            font: inherit;
          }

          .InstagramAutoSetupStarter .starter-page {
            flex: 1;

            width: 100%;
            max-width: 1120px;
            min-height: 0;

            margin: 0 auto;
            padding: 52px 28px 64px;
          }

          .InstagramAutoSetupStarter .starter-hero {
            max-width: 940px;
            margin: 0 auto;
            text-align: center;
          }

          .InstagramAutoSetupStarter .starter-promo-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 7px 12px;
            margin-bottom: 20px;
            border: 1px solid #dbe3f2;
            border-radius: 999px;
            background: #ffffff;
            color: #4f46e5;
            font-size: 12px;
            font-weight: 700;
            box-shadow: 0 4px 16px rgba(35, 50, 88, 0.05);
          }

          .InstagramAutoSetupStarter .starter-promo-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #6366f1;
            box-shadow: 0 0 0 4px #eef2ff;
          }

          .InstagramAutoSetupStarter .starter-title {
            margin: 0;
            font-size: clamp(34px, 4.7vw, 58px);
            line-height: 1.22;
            letter-spacing: -0.045em;
            color: #12192b;
            font-weight: 800;
          }

          .InstagramAutoSetupStarter .starter-title-row {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            gap: 20px;
          }

          .InstagramAutoSetupStarter .starter-title-suffix {
            font-size: 40px;
            letter-spacing: 0;
          }

          .InstagramAutoSetupStarter .starter-title-accent {
            display: inline-block;
            margin-top: 4px;
            background: linear-gradient(
              90deg,
              #4f46e5 0%,
              #7c3aed 52%,
              #0891b2 100%
            );
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
          }

          .InstagramAutoSetupStarter .starter-subtitle {
            max-width: 760px;
            margin: 22px auto 0;
            color: var(--starter-muted);
            font-size: 17px;
            line-height: 1.75;
            letter-spacing: -0.015em;
          }

          .InstagramAutoSetupStarter .starter-input-card {
            width: 100%;
            margin-top: 38px;
            padding: 22px;
            border: 1px solid var(--starter-border);
            border-radius: 20px;
            background: var(--starter-card);
            box-shadow:
              0 16px 45px rgba(31, 43, 71, 0.08),
              0 2px 8px rgba(31, 43, 71, 0.04);
            text-align: left;
          }

          .InstagramAutoSetupStarter .starter-tabs {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            padding-bottom: 16px;
            margin-bottom: 18px;
            border-bottom: 1px solid #edf0f5;
          }

          .InstagramAutoSetupStarter .starter-tab-button {
            min-height: 40px;
            padding: 9px 15px;
            border: 1px solid transparent;
            border-radius: 9px;
            background: transparent;
            color: #697386;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            transition:
              background-color 0.18s ease,
              border-color 0.18s ease,
              color 0.18s ease,
              box-shadow 0.18s ease;
          }

          .InstagramAutoSetupStarter
            .starter-tab-button.is-active {
            border-color: #cfd4ff;
            background: var(--starter-primary-soft);
            color: #4338ca;
            box-shadow: inset 0 0 0 1px rgba(79, 70, 229, 0.04);
          }

          .InstagramAutoSetupStarter .starter-tab-button:disabled {
            cursor: default;
            opacity: 0.55;
          }

          .InstagramAutoSetupStarter .starter-panel {
            min-height: 58px;
          }

          .InstagramAutoSetupStarter .starter-action-row {
            display: flex;
            align-items: stretch;
            gap: 12px;
          }

          .InstagramAutoSetupStarter .starter-link-input-wrap {
            position: relative;
            flex: 1;
            min-width: 0;
          }

          .InstagramAutoSetupStarter .starter-input-icon-wrap {
            position: absolute;
            top: 50%;
            left: 16px;
            z-index: 1;
            display: flex;
            transform: translateY(-50%);
            color: #98a2b3;
            pointer-events: none;
          }

          .InstagramAutoSetupStarter .starter-input-icon,
          .InstagramAutoSetupStarter .starter-upload-icon {
            width: 20px;
            height: 20px;
          }

          .InstagramAutoSetupStarter .starter-link-input,
          .InstagramAutoSetupStarter .starter-concept-input {
            width: 100%;
            border: 1px solid var(--starter-border-strong);
            background: #ffffff;
            color: #1d2939;
            outline: none;
            transition:
              border-color 0.18s ease,
              box-shadow 0.18s ease,
              background-color 0.18s ease;
          }

          .InstagramAutoSetupStarter .starter-link-input {
            height: 54px;
            padding: 0 16px 0 46px;
            border-radius: 12px;
            font-size: 15px;
          }

          .InstagramAutoSetupStarter .starter-concept-input {
            min-height: 128px;
            padding: 15px 16px;
            border-radius: 12px;
            font-size: 15px;
            line-height: 1.65;
            resize: vertical;
          }

          .InstagramAutoSetupStarter .starter-link-input::placeholder,
          .InstagramAutoSetupStarter
            .starter-concept-input::placeholder {
            color: #98a2b3;
          }

          .InstagramAutoSetupStarter .starter-link-input:focus,
          .InstagramAutoSetupStarter .starter-concept-input:focus {
            border-color: #818cf8;
            box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.11);
          }

          .InstagramAutoSetupStarter
            .starter-link-input:disabled,
          .InstagramAutoSetupStarter
            .starter-concept-input:disabled {
            background: #f8fafc;
            color: #98a2b3;
          }

          .InstagramAutoSetupStarter .starter-submit-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            flex: 0 0 auto;
            min-width: 184px;
            min-height: 54px;
            padding: 0 24px;
            border: 0;
            border-radius: 12px;
            background: linear-gradient(
              135deg,
              #4f46e5 0%,
              #5b5ee8 55%,
              #0891b2 140%
            );
            color: #ffffff;
            font-size: 14px;
            font-weight: 800;
            cursor: pointer;
            box-shadow: 0 8px 20px rgba(79, 70, 229, 0.18);
            transition:
              transform 0.18s ease,
              box-shadow 0.18s ease,
              opacity 0.18s ease;
          }

          .InstagramAutoSetupStarter
            .starter-submit-button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 11px 24px rgba(79, 70, 229, 0.23);
          }

          .InstagramAutoSetupStarter
            .starter-submit-button:active:not(:disabled) {
            transform: translateY(0);
          }

          .InstagramAutoSetupStarter
            .starter-submit-button:disabled {
            cursor: wait;
            opacity: 0.64;
            box-shadow: none;
          }

          .InstagramAutoSetupStarter .starter-arrow-icon {
            width: 17px;
            height: 17px;
          }

          .InstagramAutoSetupStarter .starter-file-dropzone {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            flex: 1;
            min-width: 0;
            min-height: 54px;
            padding: 12px 18px;
            border: 1.5px dashed #cbd5e1;
            border-radius: 12px;
            background: #fbfcfe;
            color: #667085;
            cursor: pointer;
            outline: none;
            transition:
              border-color 0.18s ease,
              background-color 0.18s ease,
              box-shadow 0.18s ease;
          }

          .InstagramAutoSetupStarter
            .starter-file-dropzone:hover,
          .InstagramAutoSetupStarter
            .starter-file-dropzone:focus-visible,
          .InstagramAutoSetupStarter
            .starter-file-dropzone.is-dragging {
            border-color: #818cf8;
            background: #f5f7ff;
            box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.08);
          }

          .InstagramAutoSetupStarter
            .starter-file-dropzone.has-file {
            border-style: solid;
            border-color: #a7d8c0;
            background: #f4fbf7;
            color: #18794e;
          }

          .InstagramAutoSetupStarter .starter-file-input {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }

          .InstagramAutoSetupStarter .starter-upload-icon-wrap {
            display: flex;
            flex: 0 0 auto;
            color: #6366f1;
          }

          .InstagramAutoSetupStarter .starter-file-name {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 14px;
            font-weight: 600;
          }

          .InstagramAutoSetupStarter .starter-concept-action {
            display: flex;
            justify-content: flex-end;
            margin-top: 12px;
          }

          .InstagramAutoSetupStarter .starter-error {
            margin-top: 12px;
            padding: 11px 13px;
            border: 1px solid #f4c7c3;
            border-radius: 10px;
            background: var(--starter-danger-bg);
            color: var(--starter-danger);
            font-size: 13px;
            line-height: 1.5;
          }

          .InstagramAutoSetupStarter .starter-feature-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 12px;
            margin-top: 28px;
          }

          .InstagramAutoSetupStarter .starter-feature-item {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            min-height: 48px;
            padding: 11px 14px;
            border: 1px solid #e7ebf1;
            border-radius: 12px;
            background: rgba(255, 255, 255, 0.72);
            color: #667085;
            font-size: 13px;
            font-weight: 600;
          }

          .InstagramAutoSetupStarter .starter-feature-check {
            color: #4f46e5;
            font-weight: 900;
          }

          .InstagramAutoSetupStarter .starter-visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }

          @media (max-width: 760px) {
            .InstagramAutoSetupStarter .starter-header-inner {
              padding: 14px 18px;
            }

            .InstagramAutoSetupStarter .starter-brand-name {
              font-size: 21px;
            }

            .InstagramAutoSetupStarter .starter-header-actions {
              gap: 4px;
            }

            .InstagramAutoSetupStarter .starter-header-button {
              min-height: 34px;
              padding: 0 9px;
              font-size: 12px;
            }

            .InstagramAutoSetupStarter .starter-header-button.primary {
              padding: 0 11px;
            }

            .InstagramAutoSetupStarter .starter-page {
              padding: 34px 18px 44px;
            }

            .InstagramAutoSetupStarter .starter-title {
              font-size: 24px;
              line-height: 1.4;
              text-align: center;
              word-break: keep-all;
              overflow-wrap: anywhere;
            }

            .InstagramAutoSetupStarter .starter-title-row {
              flex-direction: column;
              align-items: center;
              gap: 4px;
            }

            .InstagramAutoSetupStarter .starter-title-row > span {
              min-width: 0;
              max-width: 100%;
            }

            .InstagramAutoSetupStarter .starter-title-suffix {
              font-size: inherit;
            }

            .InstagramAutoSetupStarter .starter-title-accent {
              display: block;
              margin-top: 8px;
            }

            .InstagramAutoSetupStarter .starter-subtitle {
              margin-top: 18px;
              font-size: 15px;
              line-height: 1.68;
            }

            .InstagramAutoSetupStarter .starter-input-card {
              margin-top: 28px;
              padding: 16px;
              border-radius: 16px;
            }

            .InstagramAutoSetupStarter .starter-action-row {
              flex-direction: column;
            }

            .InstagramAutoSetupStarter .starter-submit-button {
              width: 100%;
              min-width: 0;
            }

            .InstagramAutoSetupStarter .starter-tabs {
              display: grid;
              grid-template-columns: 1fr;
            }

            .InstagramAutoSetupStarter .starter-tab-button {
              width: 100%;
              text-align: left;
            }

            .InstagramAutoSetupStarter .starter-feature-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>

        <div className="starter-shell">
          {/* <header className="starter-header">
            <div className="starter-header-inner">
              <div className="starter-brand">
                <span className="starter-brand-name">RoboMD</span>
                <span className="starter-brand-badge">AI Studio</span>
              </div>

              <div className="starter-header-actions">
                <button
                  type="button"
                  className="starter-header-button"
                  onClick={this.handleLoginClick}
                >
                  로그인
                </button>
                <button
                  type="button"
                  className="starter-header-button primary"
                  onClick={this.handleFreeStartClick}
                >
                  무료 시작하기
                </button>
              </div>
            </div>
          </header> */}

          <main className="starter-main">
            <div className="starter-page">
              <section className="starter-hero">
                <h1 className="starter-title">
                  사업계획서, 회사 홈페이지 링크 만 넣으면
                </h1>
                <h2 className="starter-title-accent">
                  마케팅전략보고서와 원하는 콘텐츠가 자동으로 완성됩니다.
                </h2>
                <p className="starter-subtitle">
                  AI가 브랜드의 핵심과 당신의 아이디어를 분석해 최적화된 기업마케팅전략보고서와 기획부터 콘텐츠 디자인, 자동화세팅까지 단 10초만에 끝내드립니다.
                </p>

            <div className="starter-input-card">
              <div className="starter-tabs">
                {/* {this.renderTabButton(
                  TAB_TYPE.LINK,
                  "상품 링크 입력"
                )} */}
                {this.renderTabButton(
                  TAB_TYPE.CONCEPT,
                  "컨셉/아이디어 입력"
                )}
                {this.renderTabButton(
                  TAB_TYPE.FILE,
                  "사업계획서 업로드"
                )}
                {this.renderTabButton(
                  TAB_TYPE.COMPANY_URL,
                  "회사 홈페이지 링크 입력"
                )}
              </div>

              <form onSubmit={this.handleSubmit}>
                {this.renderActivePanel()}

                {errorMessage && (
                  <div
                    className="starter-error"
                    role="alert"
                  >
                    {errorMessage}
                  </div>
                )}
              </form>
              <div className="starter-quota-info-wrap">
                <div className="starter-quota-info">
                  <span className="starter-quota-icon" aria-hidden="true">💡</span>
                  <p className="starter-quota-text">
                    <strong className="starter-quota-label">안내:</strong>{" "}
                    분석 및 자동화 세팅 시 멤버십의{" "}
                    <strong className="starter-quota-highlight">AI 문구 제작량(예상 소진: 약 10,000자)</strong>이 차감됩니다.
                  </p>
                </div>
              </div>
            </div>

              </section>
              <style>{`
                .InstagramAutoSetupStarter { overflow: auto; }
                .auto-setup-history { width: 100%; max-width: 1460px; margin: 25px auto 32px; padding: 20px 23px 24px; background: #fff; border: 1px solid #e5ebf5; border-radius: 17px; box-shadow: 0 7px 28px rgba(28,45,83,.035); box-sizing: border-box; }
                .auto-setup-history-heading { display: flex; align-items: center; justify-content: space-between; gap: 15px; margin-bottom: 21px; }
                .auto-setup-history-heading h2 { display: flex; gap: 11px; align-items: center; margin: 0 0 9px; font-size: 23px; font-weight: 800; letter-spacing: -.8px; color: #0a1935; }
                .auto-setup-history-heading p { margin: 0; font-size: 13px; color: #818da6; }
                .auto-setup-history-new { display: inline-block; font-size: 11px; font-weight: 800; background: #ff3560; color: white; padding: 4px 11px; border-radius: 20px; letter-spacing: 0; }
                .auto-setup-history-refresh { padding: 8px 13px; border: 1px solid #e1e6f0; border-radius: 8px; background: white; color: #53617f; font-size: 12px; cursor: pointer; white-space: nowrap; }
                .auto-setup-history-refresh:hover { background: #f7f9ff; }
                .auto-setup-history-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 12px; }
                .auto-setup-history-card { position: relative; min-width: 0; min-height: 134px; padding: 11px 12px 14px; background: #fff; border: 1px solid #e1e7f2; border-radius: 11px; transition: border-color .15s,box-shadow .15s; }
                .auto-setup-history-card:hover { border-color: #8e98ff; box-shadow: 0 5px 18px rgba(65,85,181,.09); }
                .auto-setup-history-card.is-representative { border-color: #827bff; box-shadow: 0 0 0 1px rgba(102,80,255,.15); }
                .auto-setup-history-card-actions { display: flex; align-items: center; justify-content: space-between; min-height: 31px; gap: 5px; padding-left: 42px; }
                .auto-setup-history-featured, .auto-setup-history-set-featured { display: inline-flex; align-items: center; justify-content: center; padding: 4px 8px; border-radius: 6px; font-size: 11px; font-weight: 700; white-space: nowrap; }
                .auto-setup-history-featured { background: #ffbc10; color: #25213b; }
                .auto-setup-history-set-featured { background: white; border: 1px solid #e1e7f2; color: #53617f; cursor: pointer; }
                .auto-setup-history-set-featured:hover { color: #5444e5; border-color: #adabff; }
                .auto-setup-history-menu-wrap { position: relative; margin-left: auto; }
                .auto-setup-history-more { border: none; background: #f1f4ff; color: #454acb; border-radius: 7px; width: 30px; height: 29px; line-height: 1; font-size: 15px; font-weight: 900; cursor: pointer; letter-spacing: 1px; }
                .auto-setup-history-more:hover, .auto-setup-history-more[aria-expanded="true"] { background: #e5e9ff; }
                .auto-setup-history-popup { position: absolute; top: 34px; right: -6px; z-index: 30; min-width: 128px; padding: 6px; background: white; border: 1px solid #dce4f0; border-radius: 9px; box-shadow: 0 12px 30px rgba(31,44,83,.16); }
                .auto-setup-history-popup button { width: 100%; display: flex; gap: 12px; align-items: center; padding: 9px 10px; background: none; border: 0; border-radius: 6px; text-align: left; font-size: 12px; color: #23304a; cursor: pointer; }
                .auto-setup-history-popup button:hover { background: #f3f5ff; }
                .auto-setup-history-popup button.danger { color: #f0444f; }
                .auto-setup-history-popup button.danger:hover { background: #fff1f2; }
                .auto-setup-history-card-open { width: 100%; display: flex; align-items: flex-end; gap: 12px; text-align: left; border: 0; padding: 17px 0 0; background: transparent; cursor: pointer; min-width: 0; position: relative; }
                .auto-setup-history-document { display: flex; flex: 0 0 45px; align-items: center; justify-content: center; width: 45px; height: 53px; background: #f0f2ff; color: #5b60ff; border-radius: 9px; }
                .auto-setup-history-card-copy { display: flex; flex-direction: column; align-self: center; flex: 1; gap: 8px; min-width: 0; }
                .auto-setup-history-card-copy strong { font-size: 13px; color: #122041; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
                .auto-setup-history-card-copy > span { font-size: 11px; color: #8a99b4; white-space: nowrap; }
                .auto-setup-history-card-arrow { align-self: flex-end; font-size: 25px; line-height: 1; color: #162548; margin-bottom: 3px; }
                .auto-setup-history-card-busy { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; border-radius: 6px; background: rgba(255,255,255,.86); color: #6357dc; font-size: 12px; }
                .auto-setup-history-empty,.auto-setup-history-error { padding: 25px; border: 1px dashed #dce4f1; border-radius: 10px; color: #7b88a2; font-size: 13px; text-align: center; }
                .auto-setup-history-error { color: #be3e3e; text-align: left; border-color: #f2d2d2; }
                .auto-setup-history-footer { display: flex; justify-content: center; padding-top: 17px; }
                .auto-setup-history-footer button { border: 1px solid #e1e6f0; border-radius: 9px; color: #53617f; background: #fff; padding: 8px 20px; cursor: pointer; font-size: 12px; }
                .auto-setup-history-footer button:hover { background: #f7f9ff; }
                .auto-setup-history button:disabled { opacity: .5; cursor: not-allowed; }
                @media(max-width: 1100px) { .auto-setup-history-grid { grid-template-columns: repeat(2,minmax(0,1fr)); } }
                @media(max-width: 600px) { .auto-setup-history { padding: 16px 13px; } .auto-setup-history-grid { grid-template-columns: 1fr; } .auto-setup-history-heading h2 { font-size: 19px; } .auto-setup-history-heading { align-items: start; } }
              `}</style>
              <style>{`
                /* 로보MD AI 기획 자동화 퍼블리싱 HTML 시안 기준 - 기능 로직은 유지 */
                .InstagramAutoSetupStarter {
                  --starter-bg: #f9fafb;
                  --starter-card: #fff;
                  --starter-text: #0a1a2f;
                  --starter-muted: #6b7280;
                  --starter-border: #f3f4f6;
                  --starter-border-strong: #e5e7eb;
                  --starter-primary: #4f46e5;
                  --starter-mint: #00e2e6;
                  background: #f9fafb;
                  color: #0a1a2f;
                  font-family: 'Noto Sans KR', Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                .InstagramAutoSetupStarter .starter-page {
                  max-width: 1152px;
                  padding: 48px 32px 96px;
                }
                .InstagramAutoSetupStarter .starter-hero {max-width:none; margin:0 auto 0; text-align:center; word-break:keep-all;}
                .InstagramAutoSetupStarter .starter-title {
                  font-size: clamp(18px, 2vw, 24px);
                  line-height: 1.43;
                  font-weight: 700;
                  letter-spacing: -0.03em;
                  color: #0a1a2f;
                  margin: 0 0 12px;
                }
                .InstagramAutoSetupStarter .starter-title-accent {
                  display:block;
                  font-size:clamp(22px,2.5vw,28px);
                  font-weight:900;
                  line-height:1.45;
                  letter-spacing:-0.035em;
                  margin:0 0 20px;
                  padding-bottom:4px;
                  background:linear-gradient(to right,#4f46e5,#00e2e6);
                  -webkit-background-clip:text;
                  background-clip:text;
                  -webkit-text-fill-color:transparent;
                  color:transparent;
                }
                .InstagramAutoSetupStarter .starter-subtitle {
                  max-width:672px;
                  margin:0 auto;
                  color:#6b7280;
                  font-size:clamp(14px,1.5vw,16px);
                  font-weight:500;
                  line-height:1.7;
                  letter-spacing:0;
                }
                .InstagramAutoSetupStarter .starter-input-card {
                  margin:40px 0 0;
                  padding:8px;
                  border:1px solid #f3f4f6;
                  border-radius:16px;
                  background:#fff;
                  box-shadow:0 8px 30px rgba(0,0,0,.04);
                  text-align:left;
                  overflow:visible;
                }
                /* 멤버십 AI 문구 제작량 안내 - HTML 시안 동일 영역 */
                .InstagramAutoSetupStarter .starter-quota-info-wrap {
                  padding: 0 12px 12px;
                }
                .InstagramAutoSetupStarter .starter-quota-info {
                  display: flex;
                  align-items: flex-start;
                  gap: 10px;
                  padding: 12px 16px;
                  border: 1px solid rgba(0,226,230,.2);
                  border-radius: 8px;
                  background: rgba(0,226,230,.05);
                  color: #4b5563;
                  text-align: left;
                  word-break: keep-all;
                }
                .InstagramAutoSetupStarter .starter-quota-icon {
                  flex-shrink: 0;
                  font-size: 16px;
                  line-height: 1;
                  margin-top: 2px;
                }
                .InstagramAutoSetupStarter .starter-quota-text {
                  margin: 0;
                  font-size: clamp(12.5px,1.2vw,14px);
                  font-weight: 500;
                  line-height: 1.375;
                }
                .InstagramAutoSetupStarter .starter-quota-label {
                  color: #0a1a2f;
                  font-weight: 700;
                }
                .InstagramAutoSetupStarter .starter-quota-highlight {
                  color: #00bfc2;
                  font-weight: 700;
                }
                /* Tabs: HTML의 밑줄형 디자인 + overflow-x-auto 제거, 좁으면 줄바꿈 */
                .InstagramAutoSetupStarter .starter-tabs {
                  display:flex;
                  flex-wrap:wrap;
                  overflow-x:visible;
                  overflow-y:visible;
                  gap:0;
                  min-width:0;
                  margin:0;
                  padding:8px 8px 0;
                  border-bottom:1px solid #f3f4f6;
                  white-space:normal;
                  word-break:keep-all;
                }
                .InstagramAutoSetupStarter .starter-tab-button {
                  position:relative;
                  flex:0 1 auto;
                  width:auto;
                  min-height:48px;
                  padding:12px 24px 16px;
                  border:0;
                  border-radius:8px 8px 0 0;
                  background:transparent;
                  color:#6b7280;
                  font-size:16px;
                  font-weight:500;
                  text-align:center;
                  white-space:nowrap;
                  box-shadow:none;
                  transition:color .2s,background-color .2s;
                }
                .InstagramAutoSetupStarter
                .starter-tab-button:not(.is-active):hover:not(:disabled) {
                    color: #1f2937;
                    background: #f9fafb;
                }
                .InstagramAutoSetupStarter .starter-tab-button.is-active {
                  color:#4f46e5;
                  font-weight:700;
                  border:0;
                  background:linear-gradient(to right,#4f46e5,#00e2e6);
                  -webkit-background-clip:text;
                  background-clip:text;
                  -webkit-text-fill-color:transparent;
                  box-shadow:none;
                }
                .InstagramAutoSetupStarter .starter-tab-button.is-active::after {
                  content:'';
                  position:absolute;
                  bottom:-1px;
                  left:0;
                  right:0;
                  height:3px;
                  border-radius:3px 3px 0 0;
                  background:linear-gradient(to right,#4f46e5,#00e2e6);
                }
                .InstagramAutoSetupStarter .starter-panel {min-height:0; padding:16px 12px;}
                .InstagramAutoSetupStarter .starter-action-row {gap:12px;align-items:stretch;}
                .InstagramAutoSetupStarter .starter-file-dropzone {
                  min-height:56px;
                  padding:clamp(12px,1.5vw,20px);
                  border:2px dashed #e5e7eb;
                  border-radius:12px;
                  background:#f9fafb;
                  color:#6b7280;
                  box-shadow:none;
                }
                .InstagramAutoSetupStarter .starter-file-dropzone:hover,
                .InstagramAutoSetupStarter .starter-file-dropzone:focus-visible,
                .InstagramAutoSetupStarter .starter-file-dropzone.is-dragging {
                  border-color:#00e2e6;
                  background:rgba(0,226,230,.05);
                  box-shadow:0 0 15px rgba(0,226,230,.1);
                }
                .InstagramAutoSetupStarter .starter-file-dropzone.has-file {border-color:#00bfc2;background:#e0fcfd;color:#0a1a2f;}
                .InstagramAutoSetupStarter .starter-upload-icon-wrap {color:#9ca3af;}
                .InstagramAutoSetupStarter .starter-file-dropzone:hover .starter-upload-icon-wrap {color:#00bfc2;}
                .InstagramAutoSetupStarter .starter-file-name {color:inherit;font-size:clamp(13px,1.3vw,15px);font-weight:500;word-break:keep-all;}
                .InstagramAutoSetupStarter .starter-link-input {min-height:56px;height:56px;border-color:#e5e7eb;}
                .InstagramAutoSetupStarter .starter-concept-input {border-color:#e5e7eb;}
                .InstagramAutoSetupStarter .starter-link-input:focus,
                .InstagramAutoSetupStarter .starter-concept-input:focus {border-color:#00bfc2;box-shadow:0 0 0 4px rgba(0,226,230,.1);}
                .InstagramAutoSetupStarter .starter-submit-button {
                  min-height:56px;
                  min-width:0;
                  padding:12px 24px;
                  border-radius:12px;
                  border:none;
                  color:#fff;
                  background:linear-gradient(135deg,#4f46e5 0%,#00e2e6 100%);
                  font-size:clamp(14px,1.4vw,16px);
                  font-weight:700;
                  box-shadow:0 10px 18px rgba(79,70,229,.20);
                  transition:filter .2s,box-shadow .2s;
                }
                .InstagramAutoSetupStarter .starter-submit-button:hover:not(:disabled) {
                  transform:none;
                  filter:brightness(1.07);
                  box-shadow:0 10px 20px rgba(0,226,230,.22);
                }
                .InstagramAutoSetupStarter .starter-arrow-icon {width:20px;height:20px;flex-shrink:0;}
                .InstagramAutoSetupStarter .starter-concept-action {margin-top:12px;}
                .InstagramAutoSetupStarter .auto-setup-history {
                  width:100%;
                  max-width:none;
                  margin:56px auto 0;
                  padding:0;
                  background:transparent;
                  border:0;
                  border-radius:0;
                  box-shadow:none;
                }
                .InstagramAutoSetupStarter .auto-setup-history-heading {display:flex;align-items:start;gap:16px;justify-content:space-between;margin-bottom:24px;}
                .InstagramAutoSetupStarter .auto-setup-history-heading h2 {display:flex;gap:12px;flex-wrap:wrap;align-items:center;margin:0 0 8px;font-size:20px;line-height:1.45;font-weight:700;color:#0a1a2f;letter-spacing:-.03em;}
                .InstagramAutoSetupStarter .auto-setup-history-heading p {margin:0;font-size:14px;font-weight:500;line-height:1.6;color:#6b7280;}
                .InstagramAutoSetupStarter .auto-setup-history-new {background:#ef4444;border-radius:999px;padding:2px 8px;color:#fff;font-size:10px;font-weight:700;letter-spacing:.06em;}
                .InstagramAutoSetupStarter .auto-setup-history-refresh {padding:8px 12px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;color:#6b7280;font-size:12px;}
                .InstagramAutoSetupStarter .auto-setup-history-refresh:hover {background:#f9fafb;color:#0a1a2f;}
                .InstagramAutoSetupStarter .auto-setup-history-grid {
                  display:grid;
                  gap:20px;
                  grid-template-columns:repeat(auto-fit,minmax(min(100%,250px),1fr));
                }
                .InstagramAutoSetupStarter .auto-setup-history-card {
                  min-height:130px;
                  padding:20px;
                  border:1px solid #e5e7eb;
                  border-radius:12px;
                  background:#fff;
                  box-shadow:0 1px 3px rgba(0,0,0,.06);
                  transition:border-color .3s,box-shadow .3s;
                }
                .InstagramAutoSetupStarter .auto-setup-history-card:hover {
                  border-color:#00e2e6;
                  box-shadow:0 4px 20px rgba(0,226,230,.15);
                }
                .InstagramAutoSetupStarter .auto-setup-history-card.is-representative {
                  border:2px solid #00e2e6;
                  padding:19px;
                  box-shadow:0 4px 20px rgba(0,226,230,.15);
                }
                .InstagramAutoSetupStarter .auto-setup-history-card.is-representative:hover {box-shadow:0 8px 25px rgba(0,226,230,.30);}
                .InstagramAutoSetupStarter .auto-setup-history-card-actions {
                  min-height:36px;
                  padding-left:0;
                  margin-bottom:16px;
                  align-items:start;
                }
                .InstagramAutoSetupStarter .auto-setup-history-featured,
                .InstagramAutoSetupStarter .auto-setup-history-set-featured {
                  min-height:28px;
                  display:inline-flex;
                  gap:4px;
                  align-items:center;
                  padding:6px 8px;
                  border-radius:4px;
                  font-size:12px;
                  font-weight:600;
                  white-space:nowrap;
                }
                .InstagramAutoSetupStarter .auto-setup-history-featured {background:#facc15;color:#0a1a2f;font-weight:700;box-shadow:0 1px 2px rgba(0,0,0,.09);}
                .InstagramAutoSetupStarter .auto-setup-history-set-featured {background:#fff;border:1px solid #e5e7eb;color:#4b5563;}
                .InstagramAutoSetupStarter .auto-setup-history-set-featured:hover {color:#00bfc2;border-color:#00e2e6;}
                .InstagramAutoSetupStarter .auto-setup-history-more {
                  display:flex;align-items:center;justify-content:center;
                  min-width:36px;min-height:36px;width:36px;height:36px;
                  padding:8px;margin:-8px -8px 0 0;
                  border:none;background:transparent;border-radius:6px;color:#9ca3af;
                  cursor:pointer;
                }
                .InstagramAutoSetupStarter .auto-setup-history-more:hover,
                .InstagramAutoSetupStarter .auto-setup-history-more[aria-expanded='true'] {color:#00bfc2;background:rgba(0,226,230,.05);}
                .InstagramAutoSetupStarter .auto-setup-history-popup {display:none;top:36px;right:0;width:128px;padding:4px 0;border:1px solid #f3f4f6;border-radius:8px;box-shadow:0 12px 26px rgba(0,0,0,.14);overflow:hidden;}
                .InstagramAutoSetupStarter .auto-setup-history-popup.is-open {display:block;}
                .InstagramAutoSetupStarter .auto-setup-history-popup button {padding:12px 16px;gap:8px;border-radius:0;font-size:14px;color:#374151;}
                .InstagramAutoSetupStarter .auto-setup-history-popup button:hover {background:#f9fafb;}
                .InstagramAutoSetupStarter .auto-setup-history-popup button.danger {border-top:1px solid #f9fafb;color:#dc2626;font-weight:500;}
                .InstagramAutoSetupStarter .auto-setup-history-popup button.danger:hover {background:#fef2f2;}
                .InstagramAutoSetupStarter .auto-setup-history-popup svg {color:#6b7280;flex-shrink:0;}
                .InstagramAutoSetupStarter .auto-setup-history-card-open {
                  display:flex;
                  width:100%;
                  align-items:center;
                  gap:12px;
                  padding:0;
                  min-height:48px;
                }
                .InstagramAutoSetupStarter .auto-setup-history-document {
                  width:40px;height:48px;flex:0 0 40px;
                  border-radius:4px;
                  background:#f9fafb;
                  color:#9ca3af;
                  transition:background-color .3s,color .3s;
                }
                .InstagramAutoSetupStarter .auto-setup-history-card.is-representative .auto-setup-history-document {background:rgba(0,226,230,.1);color:#00bfc2;}
                .InstagramAutoSetupStarter .auto-setup-history-card:hover .auto-setup-history-document {background:rgba(0,226,230,.1);color:#00bfc2;}
                .InstagramAutoSetupStarter .auto-setup-history-card-copy {align-self:center;gap:4px;}
                .InstagramAutoSetupStarter .auto-setup-history-card-copy strong {font-size:16px;font-weight:700;color:#0a1a2f;transition:color .3s;}
                .InstagramAutoSetupStarter .auto-setup-history-card:hover .auto-setup-history-card-copy strong {color:#00bfc2;}
                .InstagramAutoSetupStarter .auto-setup-history-card-copy > span {font-size:12px;font-weight:500;color:#9ca3af;}
                .InstagramAutoSetupStarter .auto-setup-history-card-arrow {display:flex;align-items:center;justify-content:center;flex:0 0 24px;width:24px;height:24px;align-self:center;margin:0;color:#9ca3af;transition:color .3s;}
                .InstagramAutoSetupStarter .auto-setup-history-card:hover .auto-setup-history-card-arrow {color:#00bfc2;}
                .InstagramAutoSetupStarter .auto-setup-history-footer {padding-top:20px;}
                .InstagramAutoSetupStarter .auto-setup-history-footer button {border:1px solid #e5e7eb;border-radius:8px;background:white;color:#4b5563;padding:10px 22px;font-size:13px;}
                .InstagramAutoSetupStarter .auto-setup-history-footer button:hover {background:#f9fafb;}
                .InstagramAutoSetupStarter .auto-setup-history-error,
                .InstagramAutoSetupStarter .auto-setup-history-empty {border-color:#e5e7eb;border-radius:12px;color:#6b7280;}
                @media(max-width:760px) {
                  .InstagramAutoSetupStarter .starter-page {padding:36px 16px 64px;}
                  .InstagramAutoSetupStarter .starter-title {font-size:clamp(18px,4vw,24px);}
                  .InstagramAutoSetupStarter .starter-title-accent {font-size:clamp(22px,5vw,28px);}
                  .InstagramAutoSetupStarter .starter-subtitle {font-size:14px;}
                  .InstagramAutoSetupStarter .starter-input-card {margin-top:32px;}
                  .InstagramAutoSetupStarter .starter-tab-button {font-size:14px;padding:10px 16px;min-height:48px;text-align:center;width:auto;}
                  .InstagramAutoSetupStarter .starter-tabs {display:flex;flex-wrap:wrap;grid-template-columns:none;padding:8px 8px 0;}
                  .InstagramAutoSetupStarter .starter-action-row {flex-direction:column;}
                  .InstagramAutoSetupStarter .starter-submit-button {width:100%;}
                  .InstagramAutoSetupStarter .starter-panel {padding:16px;}
                  .InstagramAutoSetupStarter .auto-setup-history {margin-top:36px;}
                  .InstagramAutoSetupStarter .auto-setup-history-heading h2 {font-size:20px;}
                }
                @media(max-width:360px) {
                  .InstagramAutoSetupStarter .starter-tab-button {padding:9px 10px;font-size:13px;}
                  .InstagramAutoSetupStarter .starter-panel {padding:12px 8px;}
                }
              `}</style>
              {this.renderAnalysisHistory()}
            </div>
          </main>

          {/* <footer className="starter-footer">
            &copy; iCLAVE RoboMD. All rights reserved.
          </footer> */}
        </div>

        {this.state.marketingResult && (
          <InstagramAutoSetupMarketingAnalysisModal
            analysisResult={this.state.marketingResult}
            onChange={this.handleMarketingChange}
            onClose={this.handleMarketingClose}
            onContinue={this.handleMarketingContinue}
          />
        )}

        {isSubmitting && (
          <SaveDialogV2
            id="instagram_auto_setup_save_dialog"
            content="AI가 입력한 내용을 분석해<br/>피드를 생성 중입니다."
            button_list={SAVE_DIALOG_BUTTON_LIST}
          />
        )}
      </div>
    );
  }
}

export default InstagramAutoSetupStarter;